import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Cafe } from '../../apiTypes';
import { PlusFeature } from '../../components/PlanCheck/PlanCheckPage';
import PrimaryButton from '../../components/PrimaryButton';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useLocale } from '../../contexts/locale.context';
import { updateDoc } from '../../utils/api';
import turnOffMultyUser from '../../utils/turnOffMultyUser';

const Processing = () => {
  const { cafe, tablesMap, cafeSlug } = useAdmin();
  const { lang } = useLocale();
  const navigate = useNavigate();
  const [multyUserConfirm, setMultyUserConfirm] = useState(false);
  const [multyUserTime, setMultyUserTime] = useState('20');

  const [isMultyUser, setIsMultyUser] = useState(!!window.localStorage.getItem('multyuser'));

  const toggleMultyUser = useCallback((event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (checked) {
      setMultyUserConfirm(true);
    } else {
      turnOffMultyUser();
    }
  }, []);

  const closeConfirm = useCallback(() => {
    setMultyUserConfirm(false);
  }, []);

  const confirmMultyUser = useCallback(() => {
    window.localStorage.setItem('multyuser', multyUserTime);
    setMultyUserConfirm(false);
    setIsMultyUser(true);
    window.location.reload();
  }, [multyUserTime]);

  const togglePosShifts = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      updateDoc<Cafe>(`cafes/${cafeSlug}`, {
        posShifts: checked,
      });
    },
    [cafeSlug]
  );

  const toggleTableMap = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      updateDoc<Cafe>(`cafes/${cafeSlug}`, {
        'tablesMap.enabled': checked,
      });
    },
    [cafeSlug]
  );

  const selectOrderStatuses = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      updateDoc<Cafe>(`cafes/${cafeSlug}`, {
        orderStatusesSetting: event.target.value as 'full',
      });
    },
    [cafeSlug]
  );

  return (
    <Box>
      <Typography variant="h6" sx={{ mt: 4 }}>
        {t.work_shift_tracking[lang]}
      </Typography>

      <PlusFeature>
        <FormControlLabel
          sx={{ pl: 2 }}
          name="posShifts"
          control={
            <Switch checked={cafe?.posShifts || false} value={cafe?.posShifts || false} onChange={togglePosShifts} />
          }
          label={t.open_close_pos_shifts[lang]}
        />
      </PlusFeature>

      <Typography variant="h6" sx={{ mt: 4 }}>
        {t.table_map[lang]}
      </Typography>
      <FormControlLabel
        sx={{ pl: 2 }}
        name="tablesMap.enabled"
        control={<Switch checked={tablesMap.enabled} value={tablesMap.enabled} onChange={toggleTableMap} />}
        label={t.use_table_map[lang]}
      />
      <Box sx={{ pl: 2 }}>
        <PrimaryButton color="secondary" variant="text" onClick={() => navigate('../editTableMap')}>
          {t.edit_tables_map[lang]}
        </PrimaryButton>
      </Box>

      <Typography variant="h6" sx={{ mt: 4 }}>
        {t.order_statuses[lang]}
      </Typography>
      <FormControl sx={{ pl: 2 }}>
        <RadioGroup value={cafe?.orderStatusesSetting} onChange={selectOrderStatuses}>
          <FormControlLabel value="full" control={<Radio />} label={t.all_statuses[lang]} />
          <FormControlLabel value="short" control={<Radio />} label={t.waiter_statuses_only[lang]} />
          <FormControlLabel value="off" control={<Radio />} label={t.without_statuses[lang]} />
        </RadioGroup>
      </FormControl>

      <Typography variant="h6" sx={{ mt: 4 }}>
        {t.multiuser_terminal[lang]}
      </Typography>

      <FormControlLabel
        sx={{ pl: 2 }}
        control={<Switch value={!!isMultyUser} checked={!!isMultyUser} onChange={toggleMultyUser} />}
        label={t.use_this_device_as_multiuser_terminal[lang]}
      />

      <Dialog open={multyUserConfirm} onClose={closeConfirm}>
        <DialogTitle>{t.multiuser_terminal[lang]}</DialogTitle>

        <DialogContent>
          <Typography>{t.check_pin_codes[lang]}</Typography>
          <TextField
            label={t.screenLockTimeRemaining[lang]}
            type="number"
            value={multyUserTime}
            onChange={(e) => setMultyUserTime(e.target.value)}
            sx={{ mt: 2, mb: 1, div: { backgroundColor: (t) => t.palette.background.paper } }}
            InputProps={{ endAdornment: <Typography>{t.sec[lang]}</Typography> }}
          />
        </DialogContent>

        <DialogActions>
          <Button autoFocus onClick={closeConfirm} color="primary">
            {t.cancel[lang]}
          </Button>
          <Button onClick={confirmMultyUser} color="primary">
            {t.turn_on[lang]}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Processing;
